import { forwardRef, useCallback, useImperativeHandle, useRef } from 'react';
import { get, useFormContext } from 'react-hook-form';
import { fieldCantBeEmptyMsg, invalidValueMsg, t } from '../../../common/i18n/index.js';
import { useCheckoutContext } from '../context/CheckoutContextCoCo.js';
import { BundleAddonGroupRuntimeCharacteristic, requiresMacAddress } from '../utils/characteristicsFlagsUtilsCoCo.js';
import { isValidMacAddress } from '../utils/macAddressUtilsCoCo.js';
import { getProductConfigDescriptor } from './productConfigRegistryCoCo.js';
import { deriveConfigSchema } from './useProductConfigSchemaCoCo.js';
import type { ConfigHandle, InstallationContactHandle, ProductConfigProps } from './productConfigTypesCoCo.js';
import type { ProductOfferingCartItems } from '../../../common/types/shoppingBasketCoCo.js';
import type { ContactsUpdateMethods } from '../placeOrderTypes.js';
import type { PlaceOrderFormDataCoCo, PlaceOrderItemUnitCoCo } from '../placeOrderTypesCoCo.js';
import type { ContactOrPurposeHandle } from '../steps/products/PlaceOrderContactOrPurpose/PlaceOrderContactOrPurpose.js';
import type { RefObject } from 'react';

import './productConfigSetupCoCo.js';

export interface ProductConfigRouterProps {
  basketItem: ProductOfferingCartItems;
  orderItem: PlaceOrderItemUnitCoCo;
  phoneNumbers: string[];
  phoneNumbersLoading: boolean;
  onLoadMoreNumbers: () => void;
  disabledPhoneNumbers: string[];
  initialPhoneNumber?: string;
  contactFormRefs: RefObject<Record<string, ContactOrPurposeHandle | null>>;
  installationContactRefs?: RefObject<Record<string, InstallationContactHandle | null>>;
  contactsUpdateMethods: ContactsUpdateMethods;
  productGroupCartItemIds?: string[];
  isProductGroupFirstUnit?: boolean;
  onProductOfferingRuntimeCharacteristicChange?: (groupCartItemIds: string[], selectedValue: string) => void;
  onPlanProductOfferingSpecRuntimeCharacteristicChange?: (cartItemId: string, selectedValue: string) => void;
  accessorySharedUnitKeys?: string[];
}

export const ProductConfigRouterCoCo = forwardRef<ConfigHandle, ProductConfigRouterProps>(
  (
    {
      basketItem,
      orderItem,
      phoneNumbers,
      phoneNumbersLoading,
      onLoadMoreNumbers,
      disabledPhoneNumbers,
      initialPhoneNumber,
      contactFormRefs,
      installationContactRefs,
      contactsUpdateMethods,
      productGroupCartItemIds,
      isProductGroupFirstUnit,
      onProductOfferingRuntimeCharacteristicChange,
      onPlanProductOfferingSpecRuntimeCharacteristicChange,
      accessorySharedUnitKeys,
    },
    ref
  ) => {
    const { selectedProductOffers } = useCheckoutContext();
    const offer = selectedProductOffers.find(o => o.id === basketItem.id);
    const descriptor = getProductConfigDescriptor(
      offer?.productOfferingType?.subcategory ?? basketItem.productOfferingType?.subcategory
    );
    const catalogueSchema = deriveConfigSchema(offer);
    const schema = descriptor?.alwaysCollectPurposeOfUse ? { ...catalogueSchema, needsContact: true } : catalogueSchema;

    const innerRef = useRef<ConfigHandle>(null);
    const { clearErrors, formState, getValues, setError } = useFormContext<PlaceOrderFormDataCoCo>();
    const macAddressAddons = (basketItem.bundleAddonGroup ?? [])
      .flatMap(group => group.productOffering ?? [])
      .filter(addon => addon.isSelected && requiresMacAddress(addon));
    const orderItemIndex = getValues('products.placeOrderItemUnits').findIndex(
      unit => unit.basketItemId === orderItem.basketItemId && unit.basketItemIndex === orderItem.basketItemIndex
    );
    const getMacAddressField = useCallback(
      (addonId: string) =>
        `products.placeOrderItemUnits.${orderItemIndex}.addonRuntimeCharacteristics.${addonId}.${BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS}`,
      [orderItemIndex]
    );
    const getMacAddress = (addonId: string) =>
      orderItem.addonRuntimeCharacteristics?.[addonId]?.[BundleAddonGroupRuntimeCharacteristic.MAC_ADDRESS]?.trim();
    const validateMacAddresses = () =>
      macAddressAddons.reduce((isValid, addon) => {
        const macAddressField = getMacAddressField(addon.id);
        const macAddress = getMacAddress(addon.id);
        if (macAddress && isValidMacAddress(macAddress)) {
          clearErrors(macAddressField as never);
          return isValid;
        }
        setError(macAddressField as never, {
          type: macAddress ? 'pattern' : 'required',
          message: macAddress ? t.DUPA(invalidValueMsg) : t.VPVR(fieldCantBeEmptyMsg),
        });
        return false;
      }, true);

    useImperativeHandle(ref, () => ({
      validate: async () => {
        const isMacAddressValid = validateMacAddresses();
        const isConfigValid = (await innerRef.current?.validate()) ?? true;
        return isMacAddressValid && isConfigValid;
      },
      scrollToFirstError: () => {
        const invalidAddon = macAddressAddons.find(addon => {
          const macAddress = getMacAddress(addon.id);
          return !macAddress || !isValidMacAddress(macAddress);
        });
        if (invalidAddon) {
          document.querySelector(`[name="mac-address-${invalidAddon.id}"]`)?.scrollIntoView({ behavior: 'smooth' });
          return;
        }
        innerRef.current?.scrollToFirstError();
      },
      getErrorCount: () =>
        macAddressAddons.filter(addon => get(formState.errors, getMacAddressField(addon.id))).length +
        (innerRef.current?.getErrorCount() ?? 0),
    }));

    if (!descriptor) {
      return null;
    }

    const { component: Component } = descriptor;
    const configProps: ProductConfigProps = {
      basketItem,
      orderItem,
      schema,
      phoneNumbers,
      phoneNumbersLoading,
      onLoadMoreNumbers,
      disabledPhoneNumbers,
      initialPhoneNumber,
      contactFormRefs,
      installationContactRefs,
      contactsUpdateMethods,
      productGroupCartItemIds,
      isProductGroupFirstUnit,
      onProductOfferingRuntimeCharacteristicChange,
      onPlanProductOfferingSpecRuntimeCharacteristicChange,
      accessorySharedUnitKeys,
    };

    return <Component {...configProps} ref={innerRef} />;
  }
);

ProductConfigRouterCoCo.displayName = 'ProductConfigRouterCoCo';
